import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { X, Check } from "lucide-react";
import { api } from "@/lib/api";
import PortfolioImage from "./PortfolioImage";


export default function CaseModal({ project, onClose }) {
  const dialogRef = useRef(null);
  const sheetRef = useRef(null);
  const startedAtRef = useRef(0);
  const maxScrollPctRef = useRef(0);

  useEffect(() => {
    if (!project) return;
    const dialog = dialogRef.current;
    const returnFocus = document.activeElement;
    dialog.showModal();
    const previousOverflow = document.body.style.overflow;
    const lenis = window.__lenis;
    lenis?.stop();
    document.body.style.overflow = "hidden";
    startedAtRef.current = performance.now();
    maxScrollPctRef.current = 0;

    // Fire & forget — log a project view for analytics
    // Deferring one frame prevents duplicate views during React Strict Mode's effect replay.
    const viewFrame = requestAnimationFrame(() => {
      api("/projects/view", { body: { project_id: project.id } }).catch(() => {});
    });


    const sheet = sheetRef.current;
    const onSheetScroll = () => {
      if (!sheet) return;
      const max = sheet.scrollHeight - sheet.clientHeight;
      if (max <= 0) return;
      const pct = Math.min(100, Math.max(0, (sheet.scrollTop / max) * 100));
      if (pct > maxScrollPctRef.current) maxScrollPctRef.current = pct;
    };
    sheet?.addEventListener("scroll", onSheetScroll, { passive: true });

    return () => {
      // Track engagement on close
      const duration_ms = Math.round(performance.now() - startedAtRef.current);
      const scroll_depth = Math.round(maxScrollPctRef.current);
      // Skip tiny accidental opens (<800ms)
      if (duration_ms > 800) {
        api("/projects/engage", { keepalive: true, body: {
            project_id: project.id,
            duration_ms: Math.min(duration_ms, 86400000),
            scroll_depth,
          } })
          .catch(() => {});
      }
      cancelAnimationFrame(viewFrame);
      dialog.close();
      returnFocus?.focus({ preventScroll: true });
      sheet?.removeEventListener("scroll", onSheetScroll);
      document.body.style.overflow = previousOverflow;
      lenis?.start();
    };
  }, [project, onClose]);

  return (
        <dialog
          ref={dialogRef}
          aria-labelledby="case-modal-title"
          aria-describedby="case-modal-description"
          onCancel={(event) => { event.preventDefault(); onClose(); }}
          className="modal"
          data-testid="case-modal"
          onClick={(event) => { if (event.target === event.currentTarget) onClose(); }}
        >
          <motion.div
            ref={sheetRef}
            className="modal__sheet"
            data-lenis-prevent
            initial={{ y: 80, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 60, opacity: 0 }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="modal__close"
              onClick={onClose}
              data-testid="case-modal-close"
              aria-label="Close case study"
            >
              <X size={20} strokeWidth={1.4} />
            </button>

            <header className="modal__head">
              <span className="overline">{project.category} · {project.year}</span>
              <h2 id="case-modal-title" className="modal__title serif" data-testid="case-modal-title">
                {project.title.split("—")[0]}
                {project.title.includes("—") && (
                  <span className="italic"> — {project.title.split("—")[1].trim()}</span>
                )}
              </h2>
              <p id="case-modal-description" className="modal__lede">{project.description}</p>
              <div className="modal__chips">
                {project.deliverables.map((d) => (
                  <span key={d} className="chip chip--ghost">{d}</span>
                ))}
              </div>
            </header>

            {project.caseStudy && (
              <section className="case" data-testid="case-study-block">
                <div className="case__facts">
                  <Fact label="Client" value={project.caseStudy.client} />
                  <Fact label="Duration" value={project.caseStudy.duration} />
                  <Fact label="Tools" value={project.caseStudy.tools.join(" · ")} />
                  <Fact label="Role" value={project.role} />
                </div>

                <CaseBlock
                  no="01"
                  title="The Problem"
                  testid="case-problem"
                  body={<p>{project.caseStudy.problem}</p>}
                />
                <CaseBlock
                  no="02"
                  title="The Process"
                  testid="case-process"
                  body={
                    <ol className="case-list case-list--num">
                      {project.caseStudy.process.map((step, i) => (
                        <li key={step}>
                          <span className="case-list__no overline">{String(i + 1).padStart(2, "0")}</span>
                          <span>{step}</span>
                        </li>
                      ))}
                    </ol>
                  }
                />
                <CaseBlock
                  no="03"
                  title="The Outcome"
                  testid="case-outcome"
                  body={
                    <ul className="case-list case-list--check">
                      {project.caseStudy.outcome.map((o) => (
                        <li key={o}>
                          <Check size={18} strokeWidth={1.6} />
                          <span>{o}</span>
                        </li>
                      ))}
                    </ul>
                  }
                />
              </section>
            )}

            <section className="modal__images-wrap" data-testid="case-modal-images">
              <span className="overline">◆ Selected frames</span>
              <div
                className={`case-grid case-grid--${project.id}`}
                data-testid="case-grid"
              >
                {project.images.map((img, i) => (
                  <motion.figure
                    key={img}
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, root: sheetRef.current || null, margin: "0px 0px -10% 0px" }}
                    transition={{ duration: 0.55, delay: (i % 6) * 0.04 }}
                    className="case-tile"
                  >
                    <a href={img} target="_blank" rel="noreferrer" aria-label={`Open original ${project.title} design ${i + 1} in a new tab`}>
                      <PortfolioImage src={img} alt={`${project.title} — design ${i + 1}`} sizes="(max-width: 720px) 90vw, 560px" />
                    </a>
                  </motion.figure>
                ))}
              </div>
            </section>
          </motion.div>
        </dialog>
  );
}

function Fact({ label, value }) {
  return (
    <div className="fact">
      <span className="overline">{label}</span>
      <span className="fact__val">{value}</span>
    </div>
  );
}

function CaseBlock({ no, title, body, testid }) {
  return (
    <div className="case-block" data-testid={testid}>
      <div className="case-block__head">
        <span className="overline">{no}</span>
        <h3 className="serif italic">{title}</h3>
      </div>
      <div className="case-block__body">{body}</div>
    </div>
  );
}
